import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * Filet de sécurité de rendu — ticket #66.
 *
 * Le composant existait mais n'était monté nulle part : la moindre exception
 * pendant le rendu vidait l'écran (les « pages blanches » signalées à la place
 * du rapport d'audit). Il est désormais monté autour des routes et affiche un
 * message exploitable plutôt qu'un écran vide.
 */
export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Erreur non interceptée :", error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div className="flex min-h-[60vh] items-center justify-center p-6">
                    <div className="w-full max-w-lg space-y-5 rounded-2xl border border-destructive/25 bg-card/60 p-7 text-center shadow-xl backdrop-blur-md">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-destructive/20 bg-destructive/10">
                            <AlertTriangle className="h-6 w-6 text-destructive" />
                        </div>
                        <div className="space-y-2">
                            <h1 className="text-xl font-bold text-foreground">
                                Cette page n'a pas pu s'afficher
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                Une erreur inattendue est survenue. Vos données ne sont pas perdues :
                                rechargez la page, et signalez le problème s'il persiste.
                            </p>
                        </div>
                        <div className="flex justify-center gap-3">
                            <Button onClick={() => window.location.reload()} className="rounded-xl font-bold">
                                Recharger la page
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => { window.location.href = "/"; }}
                                className="rounded-xl border-border/40 font-semibold"
                            >
                                Retour à l'accueil
                            </Button>
                        </div>
                        {this.state.error?.message && (
                            <details className="text-left">
                                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    Détail technique
                                </summary>
                                <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-muted/40 p-3 text-[11px] leading-relaxed text-muted-foreground">
                                    {this.state.error.message}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
